import { useQueryParamHelper } from '@shared/core/queryString';
import type { PartialUtmParameters, WritableUtmParameters } from '@shared/utils/utmParameters';
import { isInternalUtmSource, resolveAcquisitionAwareUtm } from '@shared/utils/utmParameters';

/**
 * The current URL's `utm_medium` exactly as written, recognised or not.
 *
 * `useUtmParameters` drops any value outside `UTM_MEDIUMS` (e.g. `cpc`), so without this a CTA
 * would replace a real paid-traffic tag with its own `web`.
 */
export const useRawUtmMedium = (): string | undefined => {
  const { getValueString } = useQueryParamHelper();
  return getValueString('utm_medium') || undefined;
};

/**
 * {@link resolveAcquisitionAwareUtm} for in-app navigation, with two extra rules.
 *
 * 1. An inbound `utm_campaign` outranks the CTA's own only when it arrived with a real acquisition
 *    source. Joy's own nav stamps source and campaign together, so without this the first nav
 *    click's campaign sticks to the URL and every later link — and its telemetry — reports a slug
 *    that contradicts where the link actually goes (`nav_menu_travel` on a click to Accommodations).
 * 2. `rawUtmMedium` is restored verbatim. A medium outside `UTM_MEDIUMS` (e.g. `cpc`) never reaches
 *    `currentUtm`, so the CTA's own `web` would win. Callers pass the same result to the outgoing
 *    URL and to the click telemetry, so dropping it here would make the two disagree.
 */
export const resolveInternalNavUtm = (currentUtm: PartialUtmParameters | undefined, internalUtm: PartialUtmParameters, rawUtmMedium?: string): WritableUtmParameters => {
  const inboundIsInternal = isInternalUtmSource(currentUtm?.utm_source);
  const resolved = resolveAcquisitionAwareUtm(inboundIsInternal ? { ...currentUtm, utm_campaign: undefined } : currentUtm, internalUtm);

  return rawUtmMedium ? { ...resolved, utm_medium: rawUtmMedium } : resolved;
};
